import type React from 'react';
import { useFeedbackCollectorParams } from '@/hooks/useFeedbackCollectorParams';
import { useFeedbackConfig } from '@/contexts/FeedbackContext';
import type { FeatureFeedbackCollectorProps } from '@/types/feedbackCollector';
import { EvoMfeApp } from '@access-evo/evo-api';

const FALLBACK_MFE_VERSION = '1.6.0';

/**
 * A reusable feedback collector component that uses EvoMfeApp.
 *
 * This component automatically sources:
 * - userId from AuthContext (user)
 * - customerName from CompanyContext (selectedCompany)
 * - product and accountToken from configuration
 *
 * Requires EvoIframeMfesProvider to be present in the component tree
 * (provided by the app-prod AppContainer).
 *
 * Only the `feature` prop needs to be specified per usage.
 *
 * @example
 * ```tsx
 * <FeatureFeedbackCollector feature="Disputes" />
 * <FeatureFeedbackCollector feature="Dashboard" />
 * ```
 */
export const FeatureFeedbackCollector: React.FC<FeatureFeedbackCollectorProps> = ({
  feature,
  className,
}) => {
  const params = useFeedbackCollectorParams(feature);
  const { mfeVersion, enabled } = useFeedbackConfig();

  if (!params || enabled === false) {
    return null;
  }

  return (
    <div className={className}>
      <EvoMfeApp
        name="evo-feature-feedback-collector"
        version={mfeVersion ?? FALLBACK_MFE_VERSION}
        parameters={params}
      />
    </div>
  );
};

export default FeatureFeedbackCollector;
